import {
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  HStack,
  NumberInput,
  NumberInputField,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';

import SelectSearch from 'components/fields/SelectSearch';
import { useAnswerCodes, ValidationEntityProvider } from '../../hooks';
import ValidatedField from '../../components/ValidatedField';
import PostalCodeInput from '../../components/PostalCodeInput';
import ValidateAddressModal, {
  type ValidatedAddress,
} from '../../components/ValidateAddressModal';
import SectionCard from '../../components/SectionCard';
import type { LocationCardProps } from './types';
import { SUPPORTED_PROVINCES } from '../policy-info/types';

const RiskTypeCard = ({
  location,
  index,
  isUpdating,
  onLocationChange,
  quoteId,
}: LocationCardProps) => {
  const { getOptions, isLoading, shouldShowConditionalField } =
    useAnswerCodes();
  const riskTypeOptions = getOptions('location_type').filter(
    (opt) => opt.value !== '',
  );
  const showOtherLocationType = shouldShowConditionalField(
    'other_location_type',
    location.location_type,
  );
  const showFloorUnit = shouldShowConditionalField(
    'floor_unit_located_on',
    location.location_type,
  );
  const showMortgages = shouldShowConditionalField(
    'number_of_mortgages',
    location.location_type,
  );

  const {
    isOpen: isValidateOpen,
    onOpen: onValidateOpen,
    onClose: onValidateClose,
  } = useDisclosure();

  const handleFieldChange = (field: string, value: string | number | null) => {
    onLocationChange(index, field, value);
  };

  const handleLocationTypeChange = (value: string | number | null) => {
    onLocationChange(index, 'location_type', value ?? '');
    if (location.other_location_type) {
      onLocationChange(index, 'other_location_type', '');
    }
  };

  const handleAddressChange = (field: string, value: string) => {
    if (location.address_validated) {
      onLocationChange(index, {
        [field]: value,
        address_validated: false,
        validated_street_number: '',
        validated_street_name: '',
        validated_city: '',
        validated_province: '',
        validated_postal_code: '',
      });
    } else {
      onLocationChange(index, field, value);
    }
  };

  const handleValidatedAddress = (validated: ValidatedAddress) => {
    const fullAddress = [validated.street_number, validated.street_name]
      .filter(Boolean)
      .join(' ');

    onLocationChange(index, {
      address: fullAddress,
      city: validated.city,
      province: validated.province,
      postal_code: validated.postal_code,
      address_validated: true,
      validated_street_number: validated.street_number,
      validated_street_name: validated.street_name,
      validated_city: validated.city,
      validated_province: validated.province,
      validated_postal_code: validated.postal_code,
    });
  };

  const savedValidation: ValidatedAddress | null = location.address_validated
    ? {
        street_number: location.validated_street_number || '',
        street_name: location.validated_street_name || '',
        city: location.validated_city || '',
        province: location.validated_province || '',
        postal_code: location.validated_postal_code || '',
      }
    : null;

  return (
    <ValidationEntityProvider
      entity={location as unknown as Record<string, unknown>}
    >
      <SectionCard title="Risk Information">
        <Grid templateColumns={{ base: '1fr', lg: '1fr 2fr' }} gap={6}>
          {/* Left Column - Risk Type */}
          <GridItem>
            <FormLabel
              fontSize="xs"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="wide"
              color="gray.500"
              mb={3}
            >
              Risk Type
            </FormLabel>

            <ValidatedField
              itemPath="location"
              fieldPath="location_type"
              label="Type"
              isDisabled={isUpdating}
              formControlProps={{ mb: 4 }}
            >
              <SelectSearch
                options={riskTypeOptions}
                value={location.location_type || ''}
                onChange={handleLocationTypeChange}
                placeholder="Select risk type..."
                isDisabled={isUpdating}
                isLoading={isLoading}
                allowClear={false}
              />
            </ValidatedField>

            {showOtherLocationType && (
              <FormControl mb={4}>
                <FormLabel fontSize="sm">Specify Type</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={location.other_location_type || ''}
                  onChange={(e) =>
                    handleFieldChange('other_location_type', e.target.value)
                  }
                  placeholder="Enter location type..."
                  isDisabled={isUpdating}
                />
              </FormControl>
            )}

            {showFloorUnit && (
              <ValidatedField
                itemPath="location"
                fieldPath="floor_unit_located_on"
                label="Floor Unit Located On"
                isDisabled={isUpdating}
                formControlProps={{ mb: 4 }}
              >
                <NumberInput
                  variant="main"
                  min={1}
                  max={99}
                  value={location.floor_unit_located_on ?? ''}
                  onChange={(_, valueAsNumber) =>
                    handleFieldChange(
                      'floor_unit_located_on',
                      isNaN(valueAsNumber) ? null : valueAsNumber,
                    )
                  }
                  isDisabled={isUpdating}
                >
                  <NumberInputField placeholder="Floor number" />
                </NumberInput>
              </ValidatedField>
            )}

            {showMortgages && (
              <FormControl>
                <FormLabel fontSize="sm">Mortgages</FormLabel>
                <NumberInput
                  variant="main"
                  min={0}
                  max={10}
                  value={location.number_of_mortgages ?? ''}
                  onChange={(_, valueAsNumber) =>
                    handleFieldChange(
                      'number_of_mortgages',
                      isNaN(valueAsNumber) ? null : valueAsNumber,
                    )
                  }
                  isDisabled={isUpdating}
                >
                  <NumberInputField placeholder="0" maxW="80px" />
                </NumberInput>
              </FormControl>
            )}
          </GridItem>

          {/* Right Column - Risk Address */}
          <GridItem>
            <HStack mb={3} justify="space-between">
              <FormLabel
                fontSize="xs"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="wide"
                color="gray.500"
                mb={0}
              >
                Risk Address
              </FormLabel>
              {location.address_validated ? (
                <Badge colorScheme="green" fontSize="xs">
                  Validated
                </Badge>
              ) : (
                <Badge colorScheme="orange" fontSize="xs">
                  Address Not Validated
                </Badge>
              )}
            </HStack>

            <Grid
              templateColumns={{ base: '1fr', md: '2fr 80px 1fr 80px 100px' }}
              gap={4}
              mb={4}
            >
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Address</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    value={location.address}
                    onChange={(e) =>
                      handleAddressChange('address', e.target.value)
                    }
                    placeholder="123 Main Street"
                    isDisabled={isUpdating}
                  />
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Unit #</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    value={location.unit_number || ''}
                    onChange={(e) =>
                      handleAddressChange('unit_number', e.target.value)
                    }
                    placeholder="Unit #"
                    isDisabled={isUpdating}
                  />
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">City</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    value={location.city}
                    onChange={(e) =>
                      handleAddressChange('city', e.target.value)
                    }
                    placeholder="City"
                    isDisabled={isUpdating}
                  />
                </FormControl>
              </GridItem>

              <GridItem>
                <ValidatedField
                  itemPath="location"
                  fieldPath="province"
                  label="Province"
                  isDisabled={isUpdating}
                >
                  <Select
                    variant="main"
                    size="sm"
                    value={location.province || ''}
                    onChange={(e) =>
                      handleAddressChange('province', e.target.value)
                    }
                    placeholder="Select"
                    isDisabled={isUpdating}
                  >
                    {SUPPORTED_PROVINCES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.value}
                      </option>
                    ))}
                  </Select>
                </ValidatedField>
              </GridItem>

              <GridItem>
                <PostalCodeInput
                  value={location.postal_code}
                  onChange={(value) =>
                    handleAddressChange('postal_code', value)
                  }
                  isDisabled={isUpdating}
                />
              </GridItem>
            </Grid>

            <HStack spacing={3}>
              <Button
                variant="outline"
                size="sm"
                onClick={onValidateOpen}
                isDisabled={
                  isUpdating ||
                  !location.address ||
                  !location.province ||
                  !location.postal_code
                }
              >
                Validate Address
              </Button>
            </HStack>
          </GridItem>
        </Grid>

        <ValidateAddressModal
          isOpen={isValidateOpen}
          onClose={onValidateClose}
          onValidate={handleValidatedAddress}
          initialAddress={location.address}
          initialCity={location.city}
          initialProvince={location.province}
          initialPostalCode={location.postal_code}
          savedValidation={savedValidation}
          isAddressValidated={location.address_validated}
        />
      </SectionCard>
    </ValidationEntityProvider>
  );
};

export default RiskTypeCard;
